import { useMemo } from 'react'
import { redtailSettingsDefaultValues, RedtailSettingsFormValues } from './model'
import { Controller, useForm } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { MissingScopeAction, type ScopeDescriptor } from '../../../../../components/ScopedBlock/models'
import { useZocksAuth, useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { Link } from 'react-router-dom'
import { Button } from '../../../../../components/Button/Button'
import { useRedtailCalendarSync } from './hooks'
import { RedtailFreeActivitiesEditor } from './RedtailFreeActivitiesEditor'
import { deriveSubmitRecords } from './freeActivities'

const CALENDAR_SYNC_SCOPE: ScopeDescriptor = { scope: 'integration:update', superScope: ['integration:admin'] }

export const RedtailCalendarSyncSettings: React.FC = () => {
  const { hasQuota, isFetching: isQuotaFetching } = useZocksQuota({ refetchQuota: false, pollingInterval: 0 })
  const hasSchedulingQuota = hasQuota('scheduling.enabled.value')

  const {
    autoSync,
    activityCodeOptions,
    categoryOptions,
    categoryIds,
    selection,
    rawFreeActivities,
    defaultSelection,
    defaultsLoaded,
    hasDefaultsError,
    isDefaultFreeActivitiesFetching,
    listsTrusted,
    isFetching,
    isSaving,
    save,
  } = useRedtailCalendarSync({ withFreeActivities: hasSchedulingQuota })
  const { hasScope } = useZocksAuth()
  const hasUpdateScope = hasScope('**', CALENDAR_SYNC_SCOPE.scope, CALENDAR_SYNC_SCOPE.superScope)

  const hasStoredFreeActivities = Array.isArray(rawFreeActivities) && rawFreeActivities.length > 0

  const serverValues = useMemo(
    () => ({
      ...redtailSettingsDefaultValues,
      inbound: {
        ...redtailSettingsDefaultValues.inbound,
        calendar: {
          enabled: autoSync,
          hideActivities: hasStoredFreeActivities,
          blockingSelection: selection,
        },
      },
    }),
    [autoSync, hasStoredFreeActivities, selection],
  )

  const form = useForm<RedtailSettingsFormValues>({ defaultValues: redtailSettingsDefaultValues, values: serverValues })

  const showDefaultsError = hasDefaultsError && !hasStoredFreeActivities

  // eslint-disable-next-line react-hooks/incompatible-library
  const calendarEnabled = form.watch('inbound.calendar.enabled')
  const hideActivities = form.watch('inbound.calendar.hideActivities')

  const onSubmit = async (data: RedtailSettingsFormValues) => {
    const freeActivities = hasSchedulingQuota
      ? deriveSubmitRecords({
          listsTrusted,
          hideActivities: data.inbound.calendar.hideActivities,
          selection: data.inbound.calendar.blockingSelection,
          rawFreeActivities,
          knownCategoryIds: categoryIds,
        })
      : null
    await save({ enabled: data.inbound.calendar.enabled, freeActivities })
  }

  const handleCancel = () => {
    form.reset()
  }

  if (isFetching || isQuotaFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h5>Calendar Sync</h5>
      <Row className='pb-2 pt-4'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your calendar automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.calendar.enabled'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={CALENDAR_SYNC_SCOPE}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <hr></hr>
      {hasSchedulingQuota && calendarEnabled ? (
        <>
          <h5 className='pt-3 pb-2'>Exclude Activities from Scheduling Calendar</h5>
          <div className='pb-3'>Turn off any activity to exclude it and free up that time for Scheduling.</div>
          <Row className='mb-4 pt-4'>
            <Col>
              <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
                <div>Choose what blocks time</div>
              </Stack>
            </Col>
            <Col md='auto'>
              <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
                <Controller
                  name='inbound.calendar.hideActivities'
                  control={form.control}
                  render={({ field }) => (
                    <Form.Switch
                      checked={field.value === true}
                      onChange={(e) => {
                        field.onChange(e)
                        if (e.target.checked && !hasStoredFreeActivities && defaultsLoaded) {
                          form.setValue('inbound.calendar.blockingSelection', defaultSelection)
                        }
                      }}
                      disabled={!hasUpdateScope || (!hasStoredFreeActivities && isDefaultFreeActivitiesFetching)}
                    />
                  )}
                />
              </Stack>
            </Col>
          </Row>
          {hideActivities ? (
            <Row className='mb-3'>
              <Col>
                {listsTrusted ? (
                  <>
                    {showDefaultsError ? (
                      <div className='paragraph-medium zocks-text-gray-600 mb-2'>
                        Couldn&apos;t load the default configuration right now, so nothing is pre-selected. You can still pick activities and categories below.
                      </div>
                    ) : null}
                    <Controller
                      name='inbound.calendar.blockingSelection'
                      control={form.control}
                      render={({ field }) => (
                        <RedtailFreeActivitiesEditor
                          selection={field.value}
                          setSelection={(updater) => field.onChange(updater(form.getValues('inbound.calendar.blockingSelection')))}
                          activityCodeOptions={activityCodeOptions}
                          categoryOptions={categoryOptions}
                          disabled={!hasUpdateScope}
                        />
                      )}
                    />
                  </>
                ) : (
                  <div className='paragraph-medium zocks-text-gray-600'>
                    Couldn&apos;t load Redtail activity types and categories right now. Your saved settings haven&apos;t changed — please try again later.
                  </div>
                )}
              </Col>
            </Row>
          ) : null}
          <hr></hr>
        </>
      ) : null}
      <Row>
        <Col>
          <div className='paragraph-medium zocks-text-gray-600'>
            Settings related to filtering by <strong>type</strong> and <strong>category</strong> are available on the{' '}
            <Link className='button-link-medium zocks-text-primary cursor-pointer' to='/account/profile-settings/meeting-assistant'>
              Event Filters
            </Link>{' '}
            page.
          </div>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isSaving}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={CALENDAR_SYNC_SCOPE}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isSaving}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
